import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Drawer, Heading, Text } from '@chakra-ui/react';
import { AgeGroup, SessionInfo } from '../../types';
import { ConfirmDialog } from '../dialog/ConfirmDialog';
import { SidebarNewChatButton } from './SidebarNewChatButton';
import { SidebarSessionButton } from './SidebarSessionButton';
import { SidebarDeleteButton } from './SidebarDeleteButton';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  onNewChat: () => void;
  ageGroup: AgeGroup;
  chatHistory: SessionInfo[];
  currentSessionId: string | null;
  onSelectChat: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
}

export const Sidebar = ({
  isOpen,
  onToggle,
  onNewChat,
  chatHistory,
  currentSessionId,
  onSelectChat,
  onDeleteSession,
}: SidebarProps) => {
  const { t } = useTranslation();
  const [hoveredSessionId, setHoveredSessionId] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);

  const handleDeleteClick = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    setSessionToDelete(sessionId);
    setShowConfirmDialog(true);
  };

  const confirmDelete = () => {
    if (sessionToDelete) {
      onDeleteSession(sessionToDelete);
      setShowConfirmDialog(false);
      setSessionToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmDialog(false);
    setSessionToDelete(null);
  };

  // タイムスタンプから日付ラベルを取得
  const getDateLabel = (timestamp: number) => {
    const sessionDate = new Date(timestamp * 1000);
    const today = new Date();

    // 今日の0時0分を取得
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const sessionStart = new Date(
      sessionDate.getFullYear(),
      sessionDate.getMonth(),
      sessionDate.getDate()
    );

    // 今日かどうかをチェック
    if (sessionStart.getTime() === todayStart.getTime()) {
      return t('sidebar.time.today');
    }

    // 今日以外は日付を表示
    const month = sessionDate.getMonth() + 1;
    const day = sessionDate.getDate();
    return t('sidebar.time.dateFormat', { month, day });
  };

  // チャット履歴をグループ化
  const groupedHistory = chatHistory.reduce(
    (acc, session) => {
      const group = getDateLabel(session.last_activity);
      if (!acc[group]) acc[group] = [];
      acc[group].push(session);
      return acc;
    },
    {} as Record<string, SessionInfo[]>
  );

  // グループを順番に並べる（今日が最初、その後は日付順）
  const groups = Object.keys(groupedHistory).sort((a, b) => {
    if (a === t('sidebar.time.today')) return -1;
    if (b === t('sidebar.time.today')) return 1;

    // グループ内の最新のタイムスタンプで比較
    const aLatest = Math.max(...groupedHistory[a].map((s) => s.last_activity));
    const bLatest = Math.max(...groupedHistory[b].map((s) => s.last_activity));
    return bLatest - aLatest;
  });

  // サイドバーコンテンツ（モバイルとデスクトップで共通）
  const sidebarContent = (
    <>
      <Box borderBottom="1px" borderColor="borders.emphasized" p={3.5}>
        <SidebarNewChatButton onClick={onNewChat} label={t('sidebar.newChat')} />
      </Box>

      <Box flex={1} overflowY="auto" p={2}>
        <Box display="flex" flexDirection="column" gap={4}>
          {groups.map((group) => {
            const sessions = groupedHistory[group];
            if (!sessions || sessions.length === 0) return null;

            return (
              <Box display="flex" flexDirection="column" gap={1} key={group}>
                <Heading
                  as="h3"
                  m={0}
                  px={3}
                  py={2}
                  fontSize="xs"
                  fontWeight="bold"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  color="text.secondary"
                  textShadow="0 1px 2px rgba(0, 0, 0, 0.1)"
                >
                  {group}
                </Heading>
                {sessions.map((session) => (
                  <Box
                    key={session.session_id}
                    position="relative"
                    display="flex"
                    maxW="full"
                    alignItems="center"
                    onMouseEnter={() => setHoveredSessionId(session.session_id)}
                    onMouseLeave={() => setHoveredSessionId(null)}
                  >
                    <SidebarSessionButton
                      sessionId={session.session_id}
                      title={session.title || t('chat.newChat')}
                      isActive={session.session_id === currentSessionId}
                      onClick={() => onSelectChat(session.session_id)}
                    />
                    {hoveredSessionId === session.session_id && (
                      <SidebarDeleteButton
                        onClick={(e) => handleDeleteClick(e, session.session_id)}
                        title={t('sidebar.delete')}
                        ariaLabel={t('sidebar.delete')}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            );
          })}
          {chatHistory.length === 0 && (
            <Box px={4} py={8} textAlign="center">
              <Text fontSize="sm" color="gray.600">
                {t('sidebar.emptyHistory')}
              </Text>
            </Box>
          )}
        </Box>
      </Box>

      <Box p={4}></Box>
    </>
  );

  return (
    <>
      {/* モバイル用Drawer */}
      <Drawer.Root
        open={isOpen}
        onOpenChange={(e) => !e.open && onToggle()}
        placement="start"
        size="sm"
      >
        <Drawer.Backdrop display={{ base: 'block', md: 'none' }} bg="blackAlpha.600" />
        <Drawer.Positioner display={{ base: 'flex', md: 'none' }}>
          <Drawer.Content
            w={70}
            minW={70}
            maxW={70}
            flexShrink={0}
            overflow="hidden"
            borderRight="1px"
            borderColor="borders.default"
            display={{ base: 'flex', md: 'none' }}
            flexDirection="column"
            bg="gradientSecondary"
          >
            <Drawer.Title srOnly>{t('sidebar.chatHistory')}</Drawer.Title>
            {sidebarContent}
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>

      {/* デスクトップ用サイドバー */}
      <Box
        as="aside"
        display={{ base: 'none', md: 'flex' }}
        w={70}
        minW={70}
        maxW={70}
        flexShrink={0}
        flexDirection="column"
        overflow="hidden"
        borderRight="1px"
        borderColor="borders.default"
        bg="gradientSecondary"
      >
        {sidebarContent}
      </Box>

      {/* 削除確認ダイアログ */}
      <ConfirmDialog isOpen={showConfirmDialog} onConfirm={confirmDelete} onCancel={cancelDelete} />
    </>
  );
};
